import React, { useEffect, useRef, useState } from "react";
import { Box, Button, Sheet, Stack, Typography } from "@mui/joy";
import { theme } from "styles/theme";
import type { TaskEditorState } from "./utils/taskEditorUtils";
import { useAgentChatThread } from "./agent/useAgentChatThread";
import {
  AgentLivingLogo,
  AssistantTurnView,
  ChatInput,
  PresetScenariosBar,
  PRESET_SCENARIOS,
  UserBubble,
} from "./agent/components";
import type { PresetScenario } from "./agent/components";

interface AgentChatPanelProps {
  taskId?: string;
  currentTask?: TaskEditorState;
  onTaskPatch: (task: TaskEditorState) => void;
}

export default function AgentChatPanel({
  taskId,
  currentTask,
  onTaskPatch,
}: AgentChatPanelProps) {
  const { turns, isStreaming, send, respondToApproval, clear, threadId } =
    useAgentChatThread(taskId, onTaskPatch);
  const [input, setInput] = useState("");
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const isDisabled = !taskId;

  useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [turns, isStreaming]);

  const handleSend = () => {
    if (isDisabled || isStreaming) return;
    const trimmed = input.trim();
    if (!trimmed) return;
    void send(trimmed, currentTask);
    setInput("");
  };

  const handlePresetPick = (scenario: PresetScenario) => {
    if (isDisabled || isStreaming) return;
    void send(scenario.message, currentTask);
    setInput("");
  };

  const showEmptyState = !isDisabled && turns.length === 0;

  return (
    <Sheet
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        borderTop: 0,
        borderBottom: 0,
        borderLeft: 0,
        borderRadius: 0,
        backgroundColor: theme.vars.palette.Surface.Forward,
        minHeight: 0,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          px: "14px",
          py: "10px",
          borderBottom: `1px solid ${theme.vars.palette.Border.Weak}`,
          background: `linear-gradient(180deg, ${theme.vars.palette.Surface.Forward} 0%, ${theme.vars.palette.Background.Level1} 100%)`,
        }}
      >
        <AgentLivingLogo size={20} active />
        <Typography
          level="title-sm"
          sx={{
            fontWeight: 700,
            flex: 1,
            color: theme.vars.palette.Content.Strong,
            fontSize: "13px",
            letterSpacing: "0.01em",
          }}
        >
          Agent
        </Typography>
        {threadId && (
          <Box
            component="span"
            title={threadId}
            sx={{
              fontFamily:
                "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
              fontSize: "10px",
              color: theme.vars.palette.Content.Weak,
            }}
          >
            #{threadId.slice(0, 6)}
          </Box>
        )}
        <Button
          size="sm"
          variant="plain"
          color="neutral"
          onClick={clear}
          disabled={isDisabled || turns.length === 0}
          sx={{ fontSize: "11px", minHeight: "24px" }}
        >
          Clear
        </Button>
      </Box>

      <Box
        ref={scrollRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: "auto",
          p: "14px 12px",
          backgroundColor: theme.vars.palette.Background.Level1,
        }}
      >
        {isDisabled ? (
          <Typography
            level="body-sm"
            sx={{
              color: theme.vars.palette.Content.Weak,
              textAlign: "center",
              mt: "32px",
            }}
          >
            활동지를 선택하면 채팅이 활성화됩니다.
          </Typography>
        ) : showEmptyState ? (
          <EmptyState onPick={handlePresetPick} />
        ) : (
          <Stack spacing={1.5}>
            {turns.map((turn, idx) => {
              const isLast = idx === turns.length - 1;
              if (turn.role === "user") {
                return <UserBubble key={turn.id} turn={turn} />;
              }
              return (
                <AssistantTurnView
                  key={turn.id}
                  turn={turn}
                  isLast={isLast}
                  isStreaming={isStreaming}
                  onDecide={(decisions) =>
                    respondToApproval(turn.id, decisions)
                  }
                />
              );
            })}
          </Stack>
        )}
      </Box>

      <PresetScenariosBar
        onPick={handlePresetPick}
        disabled={isDisabled || isStreaming}
      />

      <ChatInput
        value={input}
        onChange={setInput}
        onSubmit={handleSend}
        disabled={isDisabled || isStreaming}
        placeholder={
          isDisabled
            ? "활동지를 먼저 선택하세요"
            : isStreaming
              ? "응답 받는 중…"
              : "작업을 설명하세요 (Enter 전송, Shift+Enter 줄바꿈)"
        }
      />
    </Sheet>
  );
}

function EmptyState({ onPick }: { onPick: (s: PresetScenario) => void }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "16px",
        py: "48px",
        textAlign: "center",
      }}
    >
      <AgentLivingLogo size={64} />
      <Stack spacing={0.5} alignItems="center">
        <Typography
          level="title-sm"
          sx={{
            fontWeight: 700,
            color: theme.vars.palette.Content.Strong,
            fontSize: "14px",
          }}
        >
          Agent 와 함께 활동지 제작
        </Typography>
        <Typography
          level="body-xs"
          sx={{
            color: theme.vars.palette.Content.Weak,
            maxWidth: "260px",
            fontSize: "12px",
            lineHeight: 1.5,
          }}
        >
          아래에서 시나리오를 선택하거나 직접 메시지를 입력해보세요.
        </Typography>
      </Stack>
      <Stack spacing={0.75} sx={{ width: "100%", maxWidth: "300px" }}>
        {PRESET_SCENARIOS.slice(0, 4).map((scenario) => (
          <Box
            key={scenario.id}
            onClick={() => onPick(scenario)}
            sx={{
              px: "12px",
              py: "10px",
              borderRadius: "8px",
              backgroundColor: theme.vars.palette.Surface.Forward,
              border: `1px solid ${theme.vars.palette.Border.Weak}`,
              cursor: "pointer",
              textAlign: "left",
              transition: "all 0.15s ease",
              "&:hover": {
                borderColor: theme.vars.palette.primary.solid,
                backgroundColor: theme.vars.palette.Background.Level1,
                transform: "translateY(-1px)",
                boxShadow: "0 2px 6px rgba(0,0,0,0.06)",
              },
            }}
          >
            <Typography
              level="body-sm"
              sx={{
                fontSize: "12px",
                fontWeight: 600,
                color: theme.vars.palette.Content.Strong,
              }}
            >
              {scenario.label}
            </Typography>
            <Typography
              level="body-xs"
              sx={{
                fontSize: "11px",
                color: theme.vars.palette.Content.Weak,
                mt: "2px",
              }}
            >
              {scenario.description}
            </Typography>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
